import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Send, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { GenieMist } from "@/pages/agent-interfaces/live-agent/components/GenieMist";
import { GenieMessageMarkdown } from "@/pages/agent-interfaces/live-agent/components/GenieMessageMarkdown";
import { GenieChatInputWeb } from "@/pages/agent-interfaces/live-agent/components/GenieChatInputWeb";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

/**
 * Internal demo — Genie chat surface ideas.
 *
 * Throwaway, self-contained mockup (mirrors /internal/loading-demo) so we can
 * FEEL three takes on the full-screen text-chat surface before committing one.
 * Context: the /help takeover currently double-frames the chat (dark stage
 * header + white sheet + gradient-bordered card + card identity header), and
 * replies used to render as raw text. Markdown rendering has shipped for real;
 * this page is about the SURFACE — what the conversation sits on.
 *
 * Three concepts, all using the same live conversation state so you can type
 * in one and switch:
 *   1. Open Air  — one light sheet, one header, no card, no border. Claude-like calm.
 *   2. Aura Glass — frosted light glass floating on the brand mist. Border-free card.
 *   3. Midnight  — no card at all; the conversation lives directly on the dark stage.
 */

const BRAND = "#4E9CFF";
const SECONDARY = "#FF4DB8";
const GRADIENT = `linear-gradient(90deg, ${BRAND} 0%, ${SECONDARY} 100%)`;

type ConceptId = "open-air" | "aura" | "midnight";

interface DemoMessage {
  isUser: boolean;
  text: string;
  time: string;
}

const SEED_THREAD: DemoMessage[] = [
  {
    isUser: true,
    time: "9:06 PM",
    text: "Why are my conversation analytics and insights not showing up in the dashboard?",
  },
  {
    isUser: false,
    time: "9:06 PM",
    text: `Analytics can take a moment to appear because conversations are processed **after they end**. The usual causes:

1. **Too short** — conversations under about 30 seconds, or with no detected speech, may not generate analytics.
2. **Still processing** — basic records appear first, while summaries, sentiment and topics can take another **2–3 minutes**.
3. **Looking in the wrong spot** — conversation activity lives on the **Dashboard** and **Activity Log**; reports live under **Genie Hub → Analytics**.
4. **Filters** — clear the date range, agent, category and search filters, then try again.

Want me to walk through any of these?`,
  },
  {
    isUser: true,
    time: "9:07 PM",
    text: "Where do I find the detailed insights?",
  },
  {
    isUser: false,
    time: "9:07 PM",
    text: `Go to **Conversational Intelligence → Recent Conversations**, pick any conversation, and select **View Details**. You'll see:

- The full transcript
- A smart summary with sentiment and topics
- Any follow-ups captured during the call`,
  },
];

const CANNED_REPLIES = [
  `Happy to help with that. The quickest route:

1. Open **Genie Hub** from the sidebar.
2. Pick the Genie you want to check.
3. Everything about the conversation — transcript, summary and insights — lands there a couple of minutes after it ends.`,
  `Good question — the short version is that everything syncs automatically. If something still looks off after **a few minutes**, clearing your filters fixes it nine times out of ten.`,
];

const nowTime = () =>
  new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/* ─────────────────────────────────────────────────────────────────────────
   Shared pieces
   ───────────────────────────────────────────────────────────────────────── */

const TypingDots = ({ tone }: { tone: "light" | "dark" }) => (
  <div className="flex items-center gap-1.5 px-1 py-2">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        animate={{ opacity: [0.25, 1, 0.25], y: [0, -3, 0] }}
        transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.18, ease: "easeInOut" }}
        className="h-1.5 w-1.5 rounded-full"
        style={{ background: tone === "dark" ? "#FFFFFF" : GRADIENT, opacity: 0.6 }}
      />
    ))}
  </div>
);

const GenieMark = ({ size = 36, dark = false }: { size?: number; dark?: boolean }) => (
  <span
    className="flex flex-shrink-0 items-center justify-center overflow-hidden rounded-xl"
    style={{
      width: size,
      height: size,
      background: dark
        ? "rgba(255,255,255,0.10)"
        : `linear-gradient(135deg, ${BRAND}1a, ${SECONDARY}1a)`,
    }}
  >
    <GenieIcon name="genie-lamp" size={Math.round(size / 2)} color={BRAND} secondaryColor={SECONDARY} gradientEnabled />
  </span>
);

/** One message thread, styled per concept. Genie replies render markdown. */
const Thread = ({
  messages,
  typing,
  tone,
  userStyle,
  fontSize = 15,
}: {
  messages: DemoMessage[];
  typing: boolean;
  tone: "light" | "dark";
  /** "pill" = soft tinted bubble; "plain" = text-on-surface */
  userStyle: "pill" | "plain";
  fontSize?: number;
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lineHeight = Math.round(fontSize * 1.6);
  const dark = tone === "dark";

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length, typing]);

  return (
    <div ref={scrollRef} className="brand-scroll h-full min-h-0 w-full overflow-y-auto overscroll-contain">
      <div className="mx-auto flex w-full max-w-[640px] flex-col gap-5 px-5 pb-6 pt-6 sm:px-6">
        {messages.map((msg, idx) => (
          <div key={idx} className={cn("group flex flex-col", msg.isUser ? "items-end" : "items-start")}>
            <div className={cn("mb-1 flex items-baseline gap-1.5", msg.isUser && "flex-row-reverse")}>
              <span
                className="text-[11px] font-semibold tracking-wide"
                style={{ color: msg.isUser ? (dark ? "#9CC4FF" : "#3B82D6") : (dark ? "#FF9AD5" : "#D6409F") }}
              >
                {msg.isUser ? "You" : "Help Genie Tutor"}
              </span>
              <span className={cn("text-[11px] opacity-0 transition-opacity group-hover:opacity-100", dark ? "text-white/40" : "text-[#94A3B8]")}>
                {msg.time}
              </span>
            </div>

            {msg.isUser ? (
              <div
                className={cn(
                  "max-w-[85%] whitespace-pre-wrap text-right font-sans",
                  userStyle === "pill" && "rounded-2xl rounded-tr-md px-4 py-2.5",
                )}
                style={{
                  fontSize,
                  lineHeight: `${lineHeight}px`,
                  color: dark ? "rgba(255,255,255,0.92)" : "#111827",
                  backgroundColor:
                    userStyle === "pill" ? (dark ? "rgba(255,255,255,0.10)" : `${BRAND}14`) : undefined,
                }}
              >
                {msg.text}
              </div>
            ) : (
              <div className="max-w-[92%]">
                <GenieMessageMarkdown
                  text={msg.text}
                  fontSize={fontSize}
                  lineHeight={lineHeight}
                  color={dark ? "rgba(255,255,255,0.88)" : "#111827"}
                  accentColor={dark ? "#9CC4FF" : "#3B82D6"}
                  tone={tone}
                />
              </div>
            )}
          </div>
        ))}
        {typing && <TypingDots tone={tone} />}
      </div>
    </div>
  );
};

/** Dark glassy input for the Midnight concept (the shared pill is white-only). */
const MidnightInput = ({
  value,
  onChange,
  onSend,
}: {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
}) => {
  const canSend = !!value.trim();
  return (
    <div className="p-[1.5px] rounded-full" style={{ background: GRADIENT, boxShadow: `0 8px 30px -12px ${BRAND}73` }}>
      <div className="flex items-center gap-2 rounded-full bg-[#141A2E]/90 px-2 py-1.5 backdrop-blur-md">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canSend) {
              e.preventDefault();
              onSend();
            }
          }}
          placeholder="Type a message..."
          className="min-w-0 flex-1 bg-transparent px-3 text-white placeholder:text-white/40 outline-none"
          style={{ fontSize: 16 }}
        />
        <button
          type="button"
          onClick={() => canSend && onSend()}
          disabled={!canSend}
          className={cn("flex h-9 w-9 items-center justify-center rounded-full transition-all", canSend && "hover:scale-105")}
          style={canSend ? { background: GRADIENT } : { backgroundColor: "rgba(255,255,255,0.08)" }}
          aria-label="Send message"
        >
          <Send className={cn("h-4 w-4", canSend ? "text-white" : "text-white/40")} />
        </button>
      </div>
    </div>
  );
};

const IdentityRow = ({ dark = false, onlineInk }: { dark?: boolean; onlineInk?: string }) => (
  <div className="flex min-w-0 items-center gap-3">
    <GenieMark dark={dark} />
    <div className="min-w-0">
      <p className={cn("truncate text-[14px] font-semibold leading-tight", dark ? "text-white" : "text-[#222B45]")}>
        Help Genie Tutor
      </p>
      <span className="mt-0.5 flex items-center gap-1.5 text-[12px] leading-tight" style={{ color: onlineInk ?? (dark ? "rgba(255,255,255,0.55)" : "#64748B") }}>
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ backgroundColor: BRAND }} />
        Online
      </span>
    </div>
  </div>
);

const EndPill = ({ dark = false }: { dark?: boolean }) => (
  <button
    type="button"
    className={cn(
      "rounded-full px-3 py-1.5 text-xs font-semibold transition-colors",
      dark ? "bg-white/10 text-white/85 hover:bg-white/20" : "bg-red-50 text-red-600 hover:bg-red-100",
    )}
  >
    End chat
  </button>
);

/* ─────────────────────────────────────────────────────────────────────────
   Concepts
   ───────────────────────────────────────────────────────────────────────── */

interface ConceptProps {
  messages: DemoMessage[];
  typing: boolean;
  draft: string;
  onChangeDraft: (v: string) => void;
  onSend: () => void;
}

/** 1 — OPEN AIR. One light sheet, one header, no card, no border anywhere.
 *  The conversation IS the surface. */
const OpenAirConcept = ({ messages, typing, draft, onChangeDraft, onSend }: ConceptProps) => (
  <div className="flex h-full min-h-0 flex-col bg-[#FDFDFE]">
    <header className="flex items-center justify-between gap-3 border-b border-[#EEF1F6] px-5 py-3 sm:px-8">
      <IdentityRow />
      <div className="flex items-center gap-1.5">
        <EndPill />
        <button type="button" className="flex h-8 w-8 items-center justify-center rounded-full text-[#9CA3AF] hover:bg-black/[0.04] hover:text-[#222B45]">
          <X className="h-4 w-4" />
        </button>
      </div>
    </header>

    <div className="relative min-h-0 flex-1">
      <Thread messages={messages} typing={typing} tone="light" userStyle="pill" />
      {/* Content fades out under the composer instead of clipping mid-line. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-[#FDFDFE] to-transparent" />
    </div>

    <div className="mx-auto w-full max-w-[640px] px-5 pb-5 pt-1 sm:px-6">
      <GenieChatInputWeb value={draft} onChange={onChangeDraft} onSend={onSend} brandColor={BRAND} secondaryColor={SECONDARY} />
      <p className="mt-2 text-center text-[11px] text-[#94A3B8]">Your Genie can also talk — tap the orb any time.</p>
    </div>
  </div>
);

/** 2 — AURA GLASS. A frosted light panel floating on the brand mist.
 *  Still a card, but border-free — depth comes from blur + shadow, not lines. */
const AuraConcept = ({ messages, typing, draft, onChangeDraft, onSend }: ConceptProps) => (
  <div className="relative flex h-full min-h-0 items-center justify-center px-3 pb-3 pt-1 sm:px-6 sm:pb-6">
    <GenieMist brandColor={BRAND} secondaryColor={SECONDARY} intensity={0.75} />
    <div
      className="relative flex h-full max-h-[820px] w-full max-w-[760px] min-h-0 flex-col overflow-hidden rounded-[28px] bg-white/85 backdrop-blur-2xl"
      style={{ boxShadow: "0 40px 120px -40px rgba(11,16,32,0.55), 0 2px 10px -4px rgba(11,16,32,0.15)" }}
    >
      <header className="flex items-center justify-between gap-3 px-5 py-3.5 sm:px-6">
        <IdentityRow />
        <div className="flex items-center gap-1.5">
          <EndPill />
          <button type="button" className="flex h-8 w-8 items-center justify-center rounded-full text-[#9CA3AF] hover:bg-black/[0.04] hover:text-[#222B45]">
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="relative min-h-0 flex-1">
        <Thread messages={messages} typing={typing} tone="light" userStyle="pill" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white/85 to-transparent" />
      </div>

      <div className="px-5 pb-4 pt-1 sm:px-6">
        <GenieChatInputWeb value={draft} onChange={onChangeDraft} onSend={onSend} brandColor={BRAND} secondaryColor={SECONDARY} />
      </div>
    </div>
  </div>
);

/** 3 — MIDNIGHT. No card at all — the conversation lives directly on the
 *  dark stage. The approved immersive-dark direction, applied to chat. */
const MidnightConcept = ({ messages, typing, draft, onChangeDraft, onSend }: ConceptProps) => (
  <div className="relative flex h-full min-h-0 flex-col">
    <GenieMist brandColor={BRAND} secondaryColor={SECONDARY} intensity={0.5} />
    <header className="relative z-10 flex items-center justify-between gap-3 px-5 py-3 sm:px-8">
      <IdentityRow dark />
      <EndPill dark />
    </header>

    <div className="relative z-10 min-h-0 flex-1">
      <Thread messages={messages} typing={typing} tone="dark" userStyle="pill" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[#0B1020] to-transparent" />
    </div>

    <div className="relative z-10 mx-auto w-full max-w-[640px] px-5 pb-6 pt-1 sm:px-6">
      <MidnightInput value={draft} onChange={onChangeDraft} onSend={onSend} />
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────
   Page
   ───────────────────────────────────────────────────────────────────────── */

const CONCEPTS: Array<{ id: ConceptId; label: string; blurb: string }> = [
  { id: "open-air", label: "1 · Open Air", blurb: "One sheet, one header, zero borders" },
  { id: "aura", label: "2 · Aura Glass", blurb: "Frosted panel floating on the mist" },
  { id: "midnight", label: "3 · Midnight", blurb: "Chat lives directly on the dark stage" },
];

const ChatDemo = () => {
  const [concept, setConcept] = useState<ConceptId>("open-air");
  const [messages, setMessages] = useState<DemoMessage[]>(SEED_THREAD);
  const [typing, setTyping] = useState(false);
  const [draft, setDraft] = useState("");
  const replyIndex = useRef(0);

  const handleSend = () => {
    const text = draft.trim();
    if (!text || typing) return;
    setDraft("");
    setMessages((prev) => [...prev, { isUser: true, text, time: nowTime() }]);
    setTyping(true);
    window.setTimeout(() => {
      const reply = CANNED_REPLIES[replyIndex.current % CANNED_REPLIES.length];
      replyIndex.current += 1;
      setTyping(false);
      setMessages((prev) => [...prev, { isUser: false, text: reply, time: nowTime() }]);
    }, 1400);
  };

  const conceptProps: ConceptProps = {
    messages,
    typing,
    draft,
    onChangeDraft: setDraft,
    onSend: handleSend,
  };

  const active = CONCEPTS.find((c) => c.id === concept)!;

  return (
    <div
      className="flex h-[100dvh] flex-col overflow-hidden"
      style={{
        background: `radial-gradient(120% 90% at 20% 0%, ${BRAND}59 0%, transparent 60%),
                     radial-gradient(120% 90% at 85% 100%, ${SECONDARY}4D 0%, transparent 62%),
                     #0B1020`,
      }}
    >
      <Helmet>
        <title>Genie Chat — Surface Ideas (Internal)</title>
      </Helmet>

      {/* Demo control bar (not part of any concept) */}
      <div className="relative z-20 flex flex-wrap items-center gap-2 px-4 py-2.5 sm:px-6">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
          aria-label="Back"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <span className="mr-2 text-[13px] font-semibold text-white">Chat surface ideas</span>
        <div className="flex flex-wrap items-center gap-1.5">
          {CONCEPTS.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setConcept(c.id)}
              className={cn(
                "rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors min-h-[36px]",
                concept === c.id ? "text-white" : "bg-white/10 text-white/70 hover:bg-white/20",
              )}
              style={concept === c.id ? { background: GRADIENT } : undefined}
            >
              {c.label}
            </button>
          ))}
        </div>
        <span className="hidden text-[12px] text-white/50 sm:inline">— {active.blurb}. Type below, it answers.</span>
      </div>

      <div className="relative z-10 min-h-0 flex-1 px-2 pb-2 sm:px-4 sm:pb-4">
        {/* Open Air gets the takeover's light sheet; the others own the space. */}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={concept}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className={cn(
              "h-full min-h-0",
              concept === "open-air" && "overflow-hidden rounded-2xl shadow-2xl ring-1 ring-white/10",
            )}
          >
            {concept === "open-air" && <OpenAirConcept {...conceptProps} />}
            {concept === "aura" && <AuraConcept {...conceptProps} />}
            {concept === "midnight" && <MidnightConcept {...conceptProps} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ChatDemo;
